import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';
import type { ProductPulseTrendPoint, ProductPulseTrends7d } from '@/hooks/useProductPulse';

const CHART_CONFIG = [
    { key: 'registrations' as const, label: 'Registros', color: 'hsl(199, 89%, 48%)' },
    { key: 'captures' as const, label: 'Capturas', color: 'hsl(172, 66%, 50%)' },
    { key: 'active_users' as const, label: 'Usuarios activos', color: 'hsl(262, 83%, 58%)' },
];

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

interface ProductPulseTrendChartsProps {
    trends: ProductPulseTrends7d | undefined;
    className?: string;
}

export function ProductPulseTrendCharts({ trends, className }: ProductPulseTrendChartsProps) {
    if (!trends) return null;

    return (
        <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4', className)}>
            {CHART_CONFIG.map(({ key, label, color }) => {
                const series = (trends[key] ?? []) as ProductPulseTrendPoint[];
                const total = series.reduce((s, p) => s + p.value, 0);
                const chartData = series.map(p => ({
                    ...p,
                    label: format(typeof p.date === 'string' ? parseISO(p.date.split('T')[0]) : new Date(p.date), 'EEE d', { locale: es }),
                }));

                return (
                    <div key={key} className="rounded-xl border border-border bg-card p-4">
                        <div className="flex items-baseline justify-between mb-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                {label} · 7 días
                            </p>
                            <p className="text-lg font-bold text-foreground">{total.toLocaleString()}</p>
                        </div>
                        <div className="h-[72px]">
                            {chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                                                <stop offset="100%" stopColor={color} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            labelFormatter={(_, payload) => payload?.[0]?.payload?.label ?? ''}
                                            formatter={(v: number) => [v.toLocaleString(), label]}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="value"
                                            stroke={color}
                                            strokeWidth={2}
                                            fill={`url(#grad-${key})`}
                                            dot={false}
                                            isAnimationActive={false}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-xs text-muted-foreground pt-6 text-center">Sin datos</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
